// src/utils/statePersistence.js - Enhanced for Multi-Order Support

export const WOE_STORAGE_KEY = 'woe_form_state'; // Keep for backward compatibility
export const MULTI_ORDER_STORAGE_KEY = 'woe_multi_orders';
export const ACTIVE_ORDER_KEY = 'woe_current_order_id';
export const ORDER_LIST_KEY = 'woe_active_order_list';

// BroadcastChannel for immediate cross-tab communication
// This provides instant notification when orders are submitted/deleted
let orderBroadcastChannel = null;
try {
  orderBroadcastChannel = new BroadcastChannel('woe_order_updates');
} catch (e) {
  console.warn('BroadcastChannel not supported, falling back to storage events only');
}

// Broadcast order event to other tabs
export const broadcastOrderEvent = (eventType, data = {}) => {
  if (orderBroadcastChannel) {
    try {
      orderBroadcastChannel.postMessage({
        type: eventType,
        ...data,
        timestamp: Date.now()
      });
      console.log(`📡 Broadcasted ${eventType} event to other tabs`);
    } catch (e) {
      console.warn('Failed to broadcast order event:', e);
    }
  }
};

// Get the broadcast channel for listeners
export const getOrderBroadcastChannel = () => orderBroadcastChannel;

// Check if an order exists in localStorage (quick check without parsing full order)
export const orderExists = (orderId) => {
  try {
    const orderList = getOrderList();
    return orderList.includes(orderId);
  } catch (error) {
    console.error('❌ Failed to check order existence:', error);
    return false;
  }
};

// Generate unique order ID
export const generateOrderId = () => {
  return `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Create new order structure
export const createNewOrder = (orderType = '', customerName = '') => {
  const orderId = generateOrderId();
  const tempOrderNo = 'TEMP-' + Date.now();
  
  return {
    id: orderId,
    formData: {
      orderType,
      orderNo: tempOrderNo,
      customerName,
      phone: '',
      address: '',
      email: '',
      city: '',
      state: '',
      zip: '',
      moldId: '',
      ID: null
    },
    localCustomerId: null,
    currentSlide: 1,
    wigDetailsData: null,
    wigRoomsData: [],
    paymentData: {
      payments: [{
        id: 1,
        method: 'Credit Card',
        amount: 0,
        notes: ''
      }],
      paymentSummary: {
        totalPaid: 0,
        balanceDue: 0,
        isProcessing: false,
        isComplete: false,
        invoiceNumber: ''
      }
    },
    timestamp: Date.now(),
    lastUpdated: Date.now(),
    version: '2.0',
    status: 'draft' // draft, saved, completed
  };
};

// Get all active orders
export const getActiveOrders = () => {
  try {
    const orderListStr = localStorage.getItem(ORDER_LIST_KEY);
    if (!orderListStr) return [];
    
    const orderList = JSON.parse(orderListStr);
    const orders = [];
    
    for (const orderId of orderList) {
      const orderData = getOrderById(orderId);
      if (orderData) {
        orders.push(orderData);
      }
    }
    
    return orders
  } catch (error) {
    console.error('❌ Failed to get active orders:', error);
    return [];
  }
};

// Get specific order by ID
export const getOrderById = (orderId) => {
  try {
    const orderData = localStorage.getItem(`woe_order_${orderId}`);
    if (!orderData) return null;
    
    const parsed = JSON.parse(orderData);
    
    // Check if order is less than 24 hours old
    const maxAge = 24 * 60 * 60 * 1000;
    if (Date.now() - parsed.timestamp > maxAge) {
      console.log(`🗑️ Order ${orderId} expired, removing...`);
      deleteOrder(orderId);
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error(`❌ Failed to get order ${orderId}:`, error);
    return null;
  }
};

// Save specific order
export const saveOrder = (orderData) => {
  try {
    const orderToSave = {
      ...orderData,
      lastUpdated: Date.now()
    };
    
    localStorage.setItem(`woe_order_${orderData.id}`, JSON.stringify(orderToSave));
    
    // Update order list
    const orderList = getOrderList();
    if (!orderList.includes(orderData.id)) {
      orderList.push(orderData.id);
      localStorage.setItem(ORDER_LIST_KEY, JSON.stringify(orderList));
    }
    
    // console.log(`💾 Order ${orderData.id} saved`);
    return true;
  } catch (error) {
    console.error(`❌ Failed to save order ${orderData.id}:`, error);
    return false;
  }
};

// Get current active order ID
export const getCurrentOrderId = () => {
  return localStorage.getItem(ACTIVE_ORDER_KEY);
};

// Set current active order
export const setCurrentOrderId = (orderId) => {
  localStorage.setItem(ACTIVE_ORDER_KEY, orderId);
  console.log(`🎯 Current order set to: ${orderId}`);
};

// Get current active order data
export const getCurrentOrder = () => {
  const currentId = getCurrentOrderId();
  if (!currentId) return null;
  return getOrderById(currentId);
};

// Create and set new order as current
export const createAndSetNewOrder = (orderType = '', customerName = '') => {
  const newOrder = createNewOrder(orderType, customerName);
  saveOrder(newOrder);
  setCurrentOrderId(newOrder.id);
  console.log(`✨ Created new order: ${newOrder.id}`);
  return newOrder;
};

// Delete order
export const deleteOrder = (orderId, options = {}) => {
  try {
    // Remove from storage
    localStorage.removeItem(`woe_order_${orderId}`);

    // Remove from order list
    const orderList = getOrderList().filter(id => id !== orderId);
    localStorage.setItem(ORDER_LIST_KEY, JSON.stringify(orderList));

    // If this was the current order, switch to another or clear
    if (getCurrentOrderId() === orderId) {
      if (orderList.length > 0) {
        setCurrentOrderId(orderList[0]);
      } else {
        localStorage.removeItem(ACTIVE_ORDER_KEY);
      }
    }

    console.log(`🗑️ Order ${orderId} deleted`);

    // Broadcast to other tabs for immediate update
    // This is especially important for order submissions
    broadcastOrderEvent('ORDER_DELETED', {
      orderId,
      isSubmission: options.isSubmission || false,
      remainingOrders: orderList
    });

    return true;
  } catch (error) {
    console.error(`❌ Failed to delete order ${orderId}:`, error);
    return false;
  }
};

// Get order list
export const getOrderList = () => {
  try {
    const orderListStr = localStorage.getItem(ORDER_LIST_KEY);
    return orderListStr ? JSON.parse(orderListStr) : [];
  } catch (error) {
    console.error('❌ Failed to get order list:', error);
    return [];
  }
};

// Duplicate existing order
export const duplicateOrder = (sourceOrderId, newCustomerName = '') => {
  try {
    const sourceOrder = getOrderById(sourceOrderId);
    if (!sourceOrder) {
      console.error(`❌ Source order ${sourceOrderId} not found`);
      return null;
    }
    
    const newOrder = createNewOrder();
    
    // Copy relevant data from source order
    newOrder.formData = {
      ...sourceOrder.formData,
      orderNo: newOrder.formData.orderNo, // Keep new order number
      customerName: newCustomerName || '',
      ID: null // Reset customer ID
    };
    
    // Copy wig details but reset barcode/serial
    if (sourceOrder.wigDetailsData) {
      newOrder.wigDetailsData = {
        ...sourceOrder.wigDetailsData,
        wigData: {
          ...sourceOrder.wigDetailsData.wigData,
          barcode: '',
          serialCode: ''
        }
      };
    }
    
    newOrder.paymentData = createNewOrder().paymentData;
    newOrder.localCustomerId = null;
    newOrder.currentSlide = 1;
    
    saveOrder(newOrder);
    console.log(`📋 Duplicated order ${sourceOrderId} to ${newOrder.id}`);
    return newOrder;
  } catch (error) {
    console.error(`❌ Failed to duplicate order ${sourceOrderId}:`, error);
    return null;
  }
};

// Clean up expired orders
export const cleanupExpiredOrders = () => {
  const orderList = getOrderList();
  let removedCount = 0;
  
  orderList.forEach(orderId => {
    const order = getOrderById(orderId);
    if (!order) {
      removedCount++;
    }
  });
  
  if (removedCount > 0) {
    console.log(`🧹 Cleaned up ${removedCount} expired orders`);
  }
};

// Migration: Convert old single-order state to multi-order
export const migrateFromSingleOrder = () => {
  try {
    const oldState = localStorage.getItem(WOE_STORAGE_KEY);
    if (!oldState || getOrderList().length > 0) {
      return; // No migration needed
    }
    
    console.log('🔄 Migrating from single-order to multi-order...');
    
    const parsed = JSON.parse(oldState);
    const migratedOrder = createNewOrder();
    
    // Map old structure to new structure
    migratedOrder.formData = parsed.formData || migratedOrder.formData;
    migratedOrder.localCustomerId = parsed.localCustomerId;
    migratedOrder.currentSlide = parsed.currentSlide || 1;
    migratedOrder.wigDetailsData = parsed.wigDetailsData;
    migratedOrder.wigRoomsData = parsed.wigRoomsData || [];
    migratedOrder.paymentData = parsed.paymentData || migratedOrder.paymentData;
    
    saveOrder(migratedOrder);
    setCurrentOrderId(migratedOrder.id);
    
    // Remove old single-order state
    localStorage.removeItem(WOE_STORAGE_KEY);
    
    console.log('✅ Migration completed');
  } catch (error) {
    console.error('❌ Migration failed:', error);
  }
};

// Backward compatibility functions
// export const saveWOEState = (state) => {
//   const currentId = getCurrentOrderId();
//   if (currentId) {
//     const orderData = {
//       ...state,
//       id: currentId
//     };
//     return saveOrder(orderData);
//   } else {
//     // Fallback: create new order
//     const newOrder = createNewOrder();
//     Object.assign(newOrder, state);
//     saveOrder(newOrder);
//     setCurrentOrderId(newOrder.id);
//     return true;
//   }
// };

export const loadWOEState = () => {
  // Try multi-order first
  const currentOrder = getCurrentOrder();
  if (currentOrder) return currentOrder;
  
  // Try migration
  migrateFromSingleOrder();
  return getCurrentOrder();
};

// export const clearWOEState = () => {
//   const currentId = getCurrentOrderId();
//   if (currentId) {
//     deleteOrder(currentId);
//   }
// };

// export const hasWOEState = () => {
//   return !!getCurrentOrder();
// };

// Initialize multi-order system
export const initializeMultiOrderSystem = () => {
  cleanupExpiredOrders();
  migrateFromSingleOrder();
  
  // If no orders exist, this will be handled when user starts new order
  console.log('🏁 Multi-order system initialized');
};